import React, { useEffect, useRef } from 'react';

import { OrbitControls } from '@react-three/drei';
import { RootState, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

import { useStore } from '../../../pages/workspace/hooks';
import { setDirections } from '../hooks/cameraOrientationStore';

interface CameraControllerProps {
  children: React.ReactNode;
}

// ── Constants ────────────────────────────────────────────────────────────────
const _up = new THREE.Vector3();

// 3D dolly: scroll wheel moves camera through space
const DOLLY_SPEED = 1.5;
const DOLLY_MIN = 2;
const DOLLY_MAX = 10000;

// 2D in-plane rotation speed (radians per pixel of mouse drag)
const ORTHO_ROTATE_SPEED = 0.005;

export const CameraController: React.FC<CameraControllerProps> = ({ children }) => {
  const { camera, gl, set, size, controls } = useThree<{
    camera: THREE.Camera;
    gl: THREE.WebGLRenderer;
    set: (state: Partial<RootState>) => void;
    size: { width: number; height: number };
    controls?: OrbitControlsImpl;
  }>();
  const { isOrthographicView, targetCameraPosition, targetCameraLookAt, setTargetCameraPosition } = useStore();

  const perspectiveCamera = useRef<THREE.PerspectiveCamera | null>(null);
  const orthographicCamera = useRef<THREE.OrthographicCamera | null>(null);
  const [orbitTarget, setOrbitTarget] = React.useState<[number, number, number]>([0, 0, 0]);

  // Current 2D rotation angle (radians, persists across 2D sessions)
  const orthoAngle = useRef(0);

  // Saved 3D camera state — restored when switching back from 2D
  const savedPerspective = useRef<{
    position: THREE.Vector3;
    target: THREE.Vector3;
    up: THREE.Vector3;
  } | null>(null);

  // ── Sync arrow-key orientation store ─────────────────────────────────────
  const syncOrientation = React.useCallback(() => {
    const target = controls?.target;
    if (!target) return;

    const dx = target.x - camera.position.x;
    const dy = target.y - camera.position.y;
    const len = Math.sqrt(dx * dx + dy * dy);

    if (len >= 0.01) {
      const fx = dx / len;
      const fy = dy / len;
      setDirections([fx, fy], [fy, -fx]);
    } else {
      // Top-down: extract camera's screen-up (Y column) from world matrix
      camera.updateMatrixWorld(true);
      _up.setFromMatrixColumn(camera.matrixWorld, 1);
      const ux = _up.x;
      const uy = _up.y;
      const uLen = Math.sqrt(ux * ux + uy * uy);
      if (uLen >= 0.001) {
        const fx = ux / uLen;
        const fy = uy / uLen;
        setDirections([fx, fy], [fy, -fx]);
      }
    }
  }, [camera, controls]);

  // ── Programmatic camera movement (e.g. focus on lamp) ──────────────────
  useEffect(() => {
    if (targetCameraPosition && targetCameraLookAt) {
      camera.position.set(targetCameraPosition[0], targetCameraPosition[1], targetCameraPosition[2]);
      setOrbitTarget([targetCameraLookAt[0], targetCameraLookAt[1], targetCameraLookAt[2]]);

      if (controls) {
        controls.target.set(targetCameraLookAt[0], targetCameraLookAt[1], targetCameraLookAt[2]);
        controls.update();
      }

      syncOrientation();
      setTargetCameraPosition(null);
    }
  }, [targetCameraPosition, targetCameraLookAt, camera, controls, setTargetCameraPosition, syncOrientation]);

  // ── Initialize cameras ─────────────────────────────────────────────────
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) {
      perspectiveCamera.current = camera;

      const aspect = size.width / size.height;
      const distance = camera.position.length();
      const vFov = (camera.fov * Math.PI) / 180;
      const height = 2 * Math.tan(vFov / 2) * distance;
      const width = height * aspect;

      orthographicCamera.current = new THREE.OrthographicCamera(
        -width / 2,
        width / 2,
        height / 2,
        -height / 2,
        0.1,
        distance * 3,
      );
      const angle = orthoAngle.current;
      orthographicCamera.current.up.set(Math.cos(angle), Math.sin(angle), 0);
    }
  }, [camera, size]);

  // ── Switch camera when mode changes ────────────────────────────────────
  useEffect(() => {
    if (!perspectiveCamera.current || !orthographicCamera.current) return;

    const shouldUseOrthographic = isOrthographicView;
    const currentlyOrthographic = camera instanceof THREE.OrthographicCamera;

    if (shouldUseOrthographic && !currentlyOrthographic) {
      // ── 3D → 2D ────────────────────────────────────────────────────────
      const perspCam = perspectiveCamera.current;
      const orthoCam = orthographicCamera.current;
      const currentTarget = controls?.target?.clone() || new THREE.Vector3();

      // 1. Save perspective state for later restore
      savedPerspective.current = {
        position: perspCam.position.clone(),
        target: currentTarget.clone(),
        up: perspCam.up.clone(),
      };

      // 2. Position ortho camera directly above target (Z+ top-down)
      const distToTarget = perspCam.position.distanceTo(currentTarget);
      orthoCam.position.set(currentTarget.x, currentTarget.y, currentTarget.z + distToTarget);
      orthoCam.lookAt(currentTarget);

      // 3. Determine the 2D "up" from 3D azimuth.
      const offset = currentTarget.clone().sub(perspCam.position);
      offset.z = 0;
      const xyLen = offset.length();
      if (xyLen >= 0.01) {
        const angle = Math.atan2(offset.y, offset.x);
        const snapped = Math.round(angle / (Math.PI / 2)) * (Math.PI / 2);
        orthoAngle.current = snapped;
      }

      const upAngle = orthoAngle.current;
      orthoCam.up.set(Math.cos(upAngle), Math.sin(upAngle), 0);

      // 4. Calculate orthographic bounds to match perspective FOV at this distance
      const vFov = (perspCam.fov * Math.PI) / 180;
      const height = distToTarget * 2 * Math.tan(vFov / 2);
      const aspect = size.width / size.height;
      const width = height * aspect;

      orthoCam.left = -width / 2;
      orthoCam.right = width / 2;
      orthoCam.top = height / 2;
      orthoCam.bottom = -height / 2;
      orthoCam.near = 0.1;
      orthoCam.far = distToTarget * 3;
      orthoCam.zoom = 1;
      orthoCam.updateProjectionMatrix();

      setOrbitTarget([currentTarget.x, currentTarget.y, currentTarget.z]);
      set({ camera: orthoCam });
    } else if (shouldUseOrthographic && currentlyOrthographic) {
      // ── 2D resize — update frustum for new canvas aspect ratio ────────
      const perspCam = perspectiveCamera.current;
      const orthoCam = orthographicCamera.current;
      if (perspCam && orthoCam) {
        const currentTarget = controls?.target?.clone() || new THREE.Vector3();
        const distToTarget = orthoCam.position.distanceTo(currentTarget);
        const vFov = (perspCam.fov * Math.PI) / 180;
        const height = distToTarget * 2 * Math.tan(vFov / 2);
        const aspect = size.width / size.height;
        const width = height * aspect;

        orthoCam.left = -width / 2;
        orthoCam.right = width / 2;
        orthoCam.top = height / 2;
        orthoCam.bottom = -height / 2;
        orthoCam.updateProjectionMatrix();
      }
    } else if (!shouldUseOrthographic && currentlyOrthographic) {
      // ── 2D → 3D ────────────────────────────────────────────────────────
      const perspCam = perspectiveCamera.current;
      const currentOrthoTarget = controls?.target?.clone() || new THREE.Vector3();

      if (savedPerspective.current) {
        const saved = savedPerspective.current;

        // Apply any pan offset the user made in 2D
        const panOffset = currentOrthoTarget.clone().sub(saved.target);
        perspCam.position.copy(saved.position).add(panOffset);
        perspCam.up.copy(saved.up);
        perspCam.updateProjectionMatrix();

        const restoredTarget = saved.target.clone().add(panOffset);
        setOrbitTarget([restoredTarget.x, restoredTarget.y, restoredTarget.z]);

        savedPerspective.current = null;
      } else {
        // Fallback
        perspCam.position.copy(orthographicCamera.current!.position);
        perspCam.up.set(0, 0, 1);
        perspCam.updateProjectionMatrix();
        setOrbitTarget([currentOrthoTarget.x, currentOrthoTarget.y, currentOrthoTarget.z]);
      }

      set({ camera: perspCam });
    }
  }, [isOrthographicView, set, size, controls, camera]);

  // ── 3D dolly: scroll wheel moves camera through space ──────────────────
  // Only active in perspective mode. In ortho mode, OrbitControls handles
  // zoom correctly by changing camera.zoom (scaling the view).
  useEffect(() => {
    if (isOrthographicView) return;

    const canvas = gl.domElement;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      const target = controls?.target;
      if (!target) return;

      const delta = -Math.sign(e.deltaY) * DOLLY_SPEED;
      const direction = new THREE.Vector3().subVectors(target, camera.position);
      const dist = direction.length();

      if (delta > 0 && dist <= DOLLY_MIN) return;
      if (delta < 0 && dist >= DOLLY_MAX) return;

      direction.normalize();
      const move = direction.multiplyScalar(delta);
      camera.position.add(move);

      controls?.update();
      syncOrientation();
    };

    canvas.addEventListener('wheel', handleWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', handleWheel);
  }, [isOrthographicView, camera, gl, controls, syncOrientation]);

  // ── 2D in-plane rotation: left-click drag rotates the view ─────────────
  useEffect(() => {
    if (!isOrthographicView) return;

    const canvas = gl.domElement;
    let isDragging = false;
    let lastX = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // left button only
      // CTRL/CMD+left-drag is reserved for OrbitControls panning
      if (e.ctrlKey || e.metaKey) return;
      // Don't start 2D rotation if user is dragging a TransformControls gizmo
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__transformDragging) return;
      isDragging = true;
      lastX = e.clientX;
      canvas.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((window as any).__transformDragging) {
        isDragging = false;
        canvas.releasePointerCapture(e.pointerId);
        return;
      }
      const dx = e.clientX - lastX;
      lastX = e.clientX;

      orthoAngle.current -= dx * ORTHO_ROTATE_SPEED;

      const angle = orthoAngle.current;
      camera.up.set(Math.cos(angle), Math.sin(angle), 0);
      camera.updateMatrixWorld(true);
      controls?.update();
      syncOrientation();
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.button !== 0) return;
      isDragging = false;
      canvas.releasePointerCapture(e.pointerId);
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    return () => {
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
    };
  }, [isOrthographicView, camera, gl, controls, syncOrientation]);

  // ── Initial orientation push ───────────────────────────────────────────
  useEffect(() => {
    syncOrientation();
  }, [syncOrientation]);

  return (
    <>
      {children}
      <OrbitControls
        camera={camera}
        target={orbitTarget}
        makeDefault
        dampingFactor={0.02}
        maxDistance={10000}
        minDistance={5}
        enableDamping={false}
        rotateSpeed={0.6}
        enableZoom={isOrthographicView}
        enableRotate={!isOrthographicView}
        maxPolarAngle={Math.PI}
        minPolarAngle={0}
        onChange={syncOrientation}
      />
    </>
  );
};
